import * as React from 'react';
import {Color, ColorResult} from 'react-color';
import {PresetColors} from './preset-colors.component';
import * as _ from 'lodash';
import {toState, simpleCheckForValidColor, isValidHex} from 'react-color/lib/helpers/color';
import {DebuggerInstance} from '../debugger';
import {localization} from '../localization/localization';
import {Checkboard, EditableInput, Hue, Saturation} from "react-color/lib/components/common";

export enum ColorChangeSource {
  Saturation,
  Hue,
  Preset,
  Input
}

export type ColorChangeHandler = (color: ColorResult, source: ColorChangeSource) => void;

export interface IColorPickerProps {
  color?: Color;
  onChange?: ColorChangeHandler;
  onChangeComplete?: ColorChangeHandler;
  recentColors?: string[];
  logoColors?: string[];
}

interface IState {
  hsl: any;
  hex: any;
  rgb: any;
  hsv: any;
  oldHue: any;
  source: any;
}

class ColorPickerComponent extends React.Component<IColorPickerProps, IState> {
  private readonly debounce: ((fn: any, data: any, event: any) => void) & _.Cancelable;
  private readonly inputStyle = {
    width: '100%',
    padding: '4px 10% 3px',
    border: 'none',
    boxShadow: 'inset 0 0 0 1px #ccc',
    fontSize: '11px',
  };
  private readonly labelStyle = {
    display: 'block',
    textAlign: 'center',
    fontSize: '11px',
    color: '#222',
    paddingTop: '3px',
    paddingBottom: '4px',
    textTransform: 'capitalize',
  };
  private readonly editableStyle = {input: this.inputStyle, label: this.labelStyle}

  constructor(props: IColorPickerProps) {
    super(props);
    this.state = {
      ...toState(props.color, 0),
    };

    this.debounce = _.debounce((fn, data, event) => {
      fn(data, event)
    }, 100)
  }

  public componentWillReceiveProps(nextProps: IColorPickerProps) {
    this.setState({
      ...toState(nextProps.color, this.state.oldHue),
    })
  }

  private handleChange(data: any, source: ColorChangeSource) {
    DebuggerInstance.log('handleChange');
    DebuggerInstance.log(data);
    DebuggerInstance.log(source);

    const isValidColor: boolean = simpleCheckForValidColor(data)
    if (isValidColor) {
      const colors = toState(data, data.h || this.state.oldHue)
      this.setState(colors)
      if (this.props.onChangeComplete) {
        // this.props.onChangeComplete(colors);
        this.debounce(this.props.onChangeComplete, colors, source)
      }
      if (this.props.onChange) {
        this.props.onChange(colors, source)
      }
    }
  }

  private handleInputChange = (data: any) => {
    if (data.hex) {
      if(isValidHex(data.hex)) {
        this.handleChange(data.hex, ColorChangeSource.Input)
      }
    } else if (data.r || data.g || data.b) {
      const {r, g, b} = data;
      const rgb = {
        r: r || this.state.rgb.r,
        g: g || this.state.rgb.g,
        b: b || this.state.rgb.b,
      };
      this.handleChange(rgb, ColorChangeSource.Input)
    }
  }

  public render() {
    let recentColorsPalete: JSX.Element | undefined;
    if (this.props.recentColors && this.props.recentColors.length > 0) {
      recentColorsPalete = <PresetColors
        colors={this.props.recentColors}
        onClick={(data) => this.handleChange(data, ColorChangeSource.Preset)}
      />
    }

    DebuggerInstance.log('*****LOGO COLORS*****');
    DebuggerInstance.log(this.props.logoColors);
    let logoColorsPalete: JSX.Element | undefined;
    if (this.props.logoColors && this.props.logoColors.length > 0) {
      logoColorsPalete = <PresetColors
        colors={this.props.logoColors}
        onClick={(data) => this.handleChange(data, ColorChangeSource.Preset)}
      />
    }

    return (
      <div className="sketch-picker color-picker-root">
        <div
          style={{
            width: '100%',
            paddingBottom: '75%',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Saturation
            {...this.state}
            onChange={(data: any) => this.handleChange(data, ColorChangeSource.Saturation)}/>
        </div>
        <div
          style={{
            display: 'flex'
          }}
          className="flexbox-fix"
        >
          <div
            style={{
              padding: '4px 0',
              flex: '1'
            }}
          >
            <div
              style={{
                position: 'relative',
                height: '24px',
                overflow: 'hidden'
              }}
            >
              <Hue {...this.state}
                   onChange={(data: any) => this.handleChange(data, ColorChangeSource.Hue)}/>
            </div>
          </div>
          <div
            style={{
              width: '24px',
              height: '24px',
              position: 'relative',
              marginTop: '4px',
              marginLeft: '4px',
              borderRadius: '3px'
            }}
          >
            <Checkboard/>
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                borderRadius: '2px',
                background: `${this.props.color}`,
                boxShadow:
                  'inset 0 0 0 1px rgba(0,0,0,.15), inset 0 0 4px rgba(0,0,0,.25)'
              }}
            />
          </div>
        </div>
        <div style={{
          display: 'flex',
          paddingTop: '4px',
        }} className="flexbox-fix">
          <div style={{flex: '4'}}>
            <EditableInput
              style={this.editableStyle as any}
              label="hex"
              value={this.state.hex.replace('#', '')}
              onChange={this.handleInputChange}
            />
          </div>
          <div style={{flex: '1', paddingLeft: '6px'}}>
            <EditableInput
              style={this.editableStyle as any}
              label="r"
              value={this.state.rgb.r}
              onChange={this.handleInputChange}
              dragLabel="true"
              dragMax="255"
            />
          </div>
          <div style={{flex: '1', paddingLeft: '6px'}}>
            <EditableInput
              style={this.editableStyle as any}
              label="g"
              value={this.state.rgb.g}
              onChange={this.handleInputChange}
              dragLabel="true"
              dragMax="255"
            />
          </div>
          <div style={{flex: '1', paddingLeft: '6px'}}>
            <EditableInput
              style={this.editableStyle as any}
              label="b"
              value={this.state.rgb.b}
              onChange={this.handleInputChange}
              dragLabel="true"
              dragMax="255"
            />
          </div>
        </div>
        <div className="pallete-label">{localization.editorLoc.logoEditorContent.recentColorsLabel}</div>
        {recentColorsPalete}
        <div className="pallete-label">{localization.editorLoc.logoEditorContent.motiveColorsLabel}</div>
        {logoColorsPalete}
      </div>
    );
  }
};

export const ColorPicker = (props: IColorPickerProps) => <ColorPickerComponent {...props} />



// WEBPACK FOOTER //
// ./src/App/shared/components/color-picker.component.tsx